import { forwardRef, InputHTMLAttributes } from 'react';

import { Button } from '../../Button/Button';
import { DatePicker } from '../../inputs/DatePicker/DatePicker';
import { TextInput } from '../../inputs/TextInput/TextInput';
import { DefaultFormFieldLayout } from '../DefaultFormFieldLayout/DefaultFormFieldLayout';
import type { HelperText } from '../FormFieldHelperText/FormFieldHelperText';

import { CalendarIcon } from '@heroicons/react/24/outline';
import { DateTime } from 'luxon';

// Hide the default calendar icon in the date input
const dateInputStyles = `
  input[type="date"]::-webkit-calendar-picker-indicator {
    display: none;
  }
  
  input[type="date"]::-webkit-inner-spin-button,
  input[type="date"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  input[type="date"] {
    -moz-appearance: textfield;
  }
`;

export type CoreFormFieldDateProps = {
  inputProps: InputHTMLAttributes<HTMLInputElement>;
  label?: React.ReactNode;
  error?: string;
  helperText?: HelperText;
  inline?: boolean;
};

export const CoreFormFieldDate = forwardRef(function _CoreFormFieldDate(
  { error, helperText, inline, inputProps, label }: CoreFormFieldDateProps,
  ref: React.Ref<HTMLInputElement>
) {
  const handleDateChange = (val: DateTime | undefined) => {
    if (val) {
      const dateString = val.toFormat('yyyy-MM-dd');
      const syntheticEvent = {
        target: { value: dateString, name: inputProps.name },
      } as React.ChangeEvent<HTMLInputElement>;

      inputProps.onChange?.(syntheticEvent);
    }
  };

  const parseDateValue = (
    value: typeof inputProps.value
  ): DateTime | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return DateTime.fromISO(value);
    if (value instanceof Date) return DateTime.fromJSDate(value);
    return undefined;
  };

  return (
    <DefaultFormFieldLayout
      label={label}
      error={error}
      inputId={inputProps.id}
      inline={inline}
      helperText={helperText}
    >
      <style>{dateInputStyles}</style>
      <div className="flex items-center gap-2">
        <TextInput
          data-testid="date-input"
          type="date"
          ref={ref}
          {...inputProps}
        />
        <DatePicker
          triggerElement={
            <Button
              variant="ghost-icon"
              dataTestId="calendar-btn"
              disabled={inputProps.disabled}
            >
              <CalendarIcon className="datepicker-icon h-6 w-6" />
            </Button>
          }
          onChange={handleDateChange}
          value={parseDateValue(inputProps.value)}
        />
      </div>
    </DefaultFormFieldLayout>
  );
});
